import { useState, useCallback, useEffect } from 'react';

// --- Text extraction helpers for structured elements ---

function extractDiagramText(container) {
  const nodes = [...container.querySelectorAll('.diagram-box')].map(n => n.textContent.trim());
  const labels = [...container.querySelectorAll('.arrow-label, .edge-badge')].map(l => l.textContent.trim());
  const parts = nodes.length ? [...nodes] : [];
  if (labels.length) parts.push('Connections: ' + labels.join(', '));
  return '[Diagram] ' + parts.join(' | ');
}

function extractTableText(container) {
  const rows = [...container.querySelectorAll('tr')].map(row => {
    return [...row.querySelectorAll('th, td')].map(c => c.textContent.trim()).join(' | ');
  });
  return '[Table] ' + rows.join('\n');
}

function extractDefinitionText(container) {
  const items = [...container.querySelectorAll('.definition-item')].map(item => {
    const term = item.querySelector('.definition-term')?.textContent.trim() || '';
    const desc = item.querySelector('.definition-description')?.textContent.trim() || '';
    return `${term}: ${desc}`;
  });
  return '[Definitions] ' + items.join('\n');
}

function extractFigureText(container) {
  const caption = container.querySelector('.figure-caption')?.textContent.trim() || '';
  const desc = container.querySelector('.figure-description')?.textContent.trim() || '';
  const panels = [...container.querySelectorAll('.figure-panel')].map(p => {
    const label = p.querySelector('.figure-panel-label')?.textContent.trim() || '';
    const panelDesc = p.querySelector('.figure-panel-description')?.textContent.trim() || '';
    return label + (panelDesc ? ': ' + panelDesc : '');
  });
  const parts = [caption, desc, ...panels].filter(Boolean);
  return '[Figure] ' + parts.join('\n');
}

function extractContentBlockText(container) {
  const title = container.querySelector('.ls-content-title')?.textContent.trim() || '';
  const body = container.querySelector('.ls-content-body')?.textContent.trim() || '';
  const keyPoints = [...container.querySelectorAll('.ls-key-points li')].map(li => li.textContent.trim());
  const parts = [title, body, ...keyPoints].filter(Boolean);
  return '[Content] ' + parts.join('\n');
}

function extractQuestionBlockText(container) {
  const prompt = container.querySelector('.ls-q-prompt, .ls-mcq-prompt, .ls-fb-prompt, .ls-tf-prompt, .ls-ms-prompt')?.textContent.trim() || '';
  const explanation = container.querySelector('.ls-explanation, .ls-q-explanation')?.textContent.trim() || '';
  const parts = [prompt, explanation].filter(Boolean);
  return parts.length ? '[Question] ' + parts.join('\n') : '';
}

const SELECTABLE_CONTAINERS = [
  { selector: '.element-diagram', extract: extractDiagramText },
  { selector: '.element-table-wrapper', extract: extractTableText },
  { selector: '.element-definition-list', extract: extractDefinitionText },
  { selector: '.element-figure', extract: extractFigureText },
  { selector: '.ls-content-block', extract: extractContentBlockText },
  { selector: '.ls-question-block', extract: extractQuestionBlockText },
  { selector: '.ls-slide-block', extract: (c) => '[Slide] ' + c.textContent.trim().slice(0, 500) },
];

function findSelectableContainer(target) {
  if (!target || !target.closest) return null;
  for (const { selector, extract } of SELECTABLE_CONTAINERS) {
    const container = target.closest(selector);
    if (container) return { container, extract };
  }
  return null;
}

// Walk up DOM to find source metadata attributes
function findSourceMetadata(startNode) {
  let node = startNode;
  let slideNumber = null;
  let blockIndex = null;
  let subtopicId = null;
  let domDeckId = null;
  let sourceType = 'slide';

  while (node && node !== document.body) {
    if (node.dataset) {
      if (node.dataset.slideNumber && !slideNumber) {
        slideNumber = parseInt(node.dataset.slideNumber, 10);
      }
      if (node.dataset.blockIndex && blockIndex === null) {
        blockIndex = parseInt(node.dataset.blockIndex, 10);
        sourceType = 'content_block';
      }
      if (node.dataset.subtopicId && !subtopicId) {
        subtopicId = node.dataset.subtopicId;
      }
      if (node.dataset.deckId && !domDeckId) {
        domDeckId = node.dataset.deckId;
      }
    }
    node = node.parentElement;
  }

  return { slideNumber, blockIndex, subtopicId, domDeckId, sourceType };
}

function useConceptBuilder(deckId) {
  const [active, setActive] = useState(false);
  const [selections, setSelections] = useState([]);

  const addSelection = useCallback((selectionObj) => {
    setSelections(prev => {
      // Dedup by exact text match
      if (prev.some(s => s.text === selectionObj.text)) return prev;
      return [...prev, selectionObj];
    });
  }, []);

  const removeSelection = useCallback((index) => {
    setSelections(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearSelections = useCallback(() => {
    document.querySelectorAll('.cb-selected').forEach(el => el.classList.remove('cb-selected'));
    setSelections([]);
  }, []);

  // Enable text selection on slides when builder is active
  // (global user-select: none in index.css blocks native selection)
  useEffect(() => {
    if (active) {
      document.body.classList.add('cb-text-select');
    }
    return () => {
      document.body.classList.remove('cb-text-select');
    };
  }, [active]);

  // Click handler for structured elements (diagrams, tables, definition lists)
  useEffect(() => {
    if (!active) return;

    const handleClick = (e) => {
      const match = findSelectableContainer(e.target);
      if (!match) return; // Not a structured element — let mouseup handle it

      const { container, extract } = match;
      const text = extract(container);
      if (!text || text.length < 10) return;

      // Toggle selection
      if (container.classList.contains('cb-selected')) {
        container.classList.remove('cb-selected');
        setSelections(prev => prev.filter(s => s.text !== text));
      } else {
        container.classList.add('cb-selected');
        const meta = findSourceMetadata(container);
        addSelection({
          text,
          source_type: meta.sourceType,
          deck_id: deckId || meta.domDeckId,
          slide_number: meta.slideNumber,
          block_index: meta.blockIndex,
          subtopic_id: meta.subtopicId,
        });
      }
    };

    const handleMouseUp = (e) => {
      // If click was on a selectable container, skip — the click handler owns it
      if (findSelectableContainer(e.target)) return;

      const sel = window.getSelection();
      const text = sel?.toString().trim();
      if (!text || text.length < 5) return;

      // Walk up from the selection anchor to find source metadata
      const meta = findSourceMetadata(sel.anchorNode);

      addSelection({
        text,
        source_type: meta.sourceType,
        deck_id: deckId || meta.domDeckId,
        slide_number: meta.slideNumber,
        block_index: meta.blockIndex,
        subtopic_id: meta.subtopicId,
      });

      // Don't clear the browser selection — the highlight toolbar (useHighlights)
      // also fires on mouseup and needs the user to be able to act on the
      // selection (click a color). Letting both consumers coexist.
    };

    document.addEventListener('click', handleClick);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('mouseup', handleMouseUp);
      document.querySelectorAll('.cb-selected').forEach(el => el.classList.remove('cb-selected'));
    };
  }, [active, deckId, addSelection]);

  return {
    active,
    setActive,
    selections,
    setSelections,
    addSelection,
    removeSelection,
    clearSelections,
  };
}

export default useConceptBuilder;
